import React, { useState } from "react";
import { CheckCircle2, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { QuizQuestion } from "./QuizShell";
import type { QuizProgress } from "./types";
import heroPropertyImage from "@/assets/hero-property.jpg";
import heroproperties from "@/assets/hero-properties.jpg";
import landscaping from "@/assets/property-image.jpg";
import { dummyService, serviceDummyData } from "./serviceTemplateDummy";

export interface TemplateQuizProps {
  progress: QuizProgress;
  campaignType: "property" | "service";
  propertyTemplates: any[];
  serviceTemplates: any[];
  selectedPropertyTemplateId?: string | number | null;
  selectedServiceTemplateId?: string | number | null;
  onSelectPropertyTemplate: (t: any) => void;
  onSelectServiceTemplate: (t: any) => void;
  onContinue: () => void;
  onBack: () => void;
  /** Live thumbnail components owned by the builder. */
  PropertyTemplatePreview: React.ComponentType<{ template: any; propertyData: any }>;
  ServiceTemplatePreview: React.ComponentType<{
    template: any;
    serviceDummyData: any;
    dummyService: any;
  }>;
}

const SAMPLE_PROPERTY = {
  id: 1,
  title: "Luxury Modern Estate",
  image: heroPropertyImage,
  photos: [heroproperties, landscaping, heroPropertyImage],
  price: 2343434,
  address: "123 Luxury Ave",
  bedrooms: 4,
  bathrooms: 3,
  area: 4000,
  description: "Luxury property",
  propertyListType: { id: 1, list_name: "" },
};

export const TemplateQuiz: React.FC<TemplateQuizProps> = ({
  progress,
  campaignType,
  propertyTemplates,
  serviceTemplates,
  selectedPropertyTemplateId,
  selectedServiceTemplateId,
  onSelectPropertyTemplate,
  onSelectServiceTemplate,
  onContinue,
  onBack,
  PropertyTemplatePreview,
  ServiceTemplatePreview,
}) => {
  const [photoFilter, setPhotoFilter] = useState<"all" | number>("all");
  const isProperty = campaignType === "property";
  const list = (isProperty ? propertyTemplates : serviceTemplates).filter(
    (t) => photoFilter === "all" || t.photoCount === photoFilter
  );
  const filters: { label: string; value: "all" | number }[] = isProperty
    ? [
        { label: "All", value: "all" },
        { label: "1 photo", value: 1 },
        { label: "2 photos", value: 2 },
        { label: "3 photos", value: 3 },
        { label: "4 photos", value: 4 },
      ]
    : [
        { label: "All", value: "all" },
        { label: "1 photo", value: 1 },
        { label: "2 photos", value: 2 },
      ];

  const selectedId = isProperty ? selectedPropertyTemplateId : selectedServiceTemplateId;
  const selected = (isProperty ? propertyTemplates : serviceTemplates).find(
    (t) => String(t.id) === String(selectedId ?? "")
  );

  return (
    <QuizQuestion
      position={progress.before + 1}
      total={progress.before + 1 + progress.after}
      title="Pick your look"
      hint="Choose a design for your launch. You can customize everything next."
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!selected}
      summary={selected ? `Selected: ${selected.name}` : "Select a template to continue"}
      size="xl"
    >
      <div className="mb-6 flex flex-wrap items-center justify-center gap-2">
        <span className="mr-1 text-sm font-medium text-slate-500">Photos in design:</span>
        {filters.map((f) => (
          <button
            key={f.label}
            type="button"
            onClick={() => setPhotoFilter(f.value)}
            className={cn(
              "rounded-full border px-4 py-1.5 text-sm font-medium transition",
              photoFilter === f.value
                ? "border-blue-600 bg-blue-600 text-white shadow"
                : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <p className="rounded-2xl border border-dashed bg-white p-10 text-center text-slate-500">
          No templates match this filter.
        </p>
      ) : (
        <div
          className={cn(
            "grid gap-4",
            isProperty ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 md:grid-cols-2"
          )}
        >
          {list.map((t) => {
            const isSel = String(t.id) === String(selectedId ?? "");
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => (isProperty ? onSelectPropertyTemplate(t) : onSelectServiceTemplate(t))}
                className={cn(
                  "relative overflow-hidden rounded-2xl border-2 bg-white text-left shadow-sm transition-all",
                  isSel
                    ? "border-blue-500 ring-2 ring-blue-200 shadow-md"
                    : "border-slate-200 hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
                )}
              >
                {isSel && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/25">
                    <CheckCircle2 className="h-16 w-16 text-green-400 drop-shadow-lg" />
                  </div>
                )}
                <div className="pointer-events-none">
                  {isProperty ? (
                    <PropertyTemplatePreview template={t} propertyData={SAMPLE_PROPERTY} />
                  ) : (
                    <ServiceTemplatePreview
                      template={t}
                      serviceDummyData={serviceDummyData}
                      dummyService={dummyService}
                    />
                  )}
                </div>
                <div className="flex flex-col gap-1.5 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-slate-900">{t.name}</span>
                    <span className="flex shrink-0 items-center gap-1 text-xs text-slate-600">
                      <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                      {t.rating}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {t.tag && <Badge variant="secondary">{t.tag}</Badge>}
                    {t.photoCount != null && (
                      <Badge variant="outline">
                        {t.photoCount} photo{t.photoCount > 1 ? "s" : ""}
                      </Badge>
                    )}
                  </div>
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>{t.uses?.toLocaleString?.() ?? t.uses ?? 0} uses</span>
                    <span>{t.category}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </QuizQuestion>
  );
};
